import React, { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';

const CRAFT_IMG = "/meserie.jpg";

function AnimateOnView({ children, delay = 0 }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay }}
    >
      {children}
    </motion.div>
  );
}

function AnimatedCounter({ target, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-60px' });
  const numTarget = parseInt(target.replace(/\D/g, ''), 10);

  useEffect(() => {
    if (!isInView || !numTarget) return;
    let start = 0;
    const step = Math.max(1, Math.floor(numTarget / (duration / 16)));
    const timer = setInterval(() => {
      start += step;
      if (start >= numTarget) {
        setCount(numTarget);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [isInView, numTarget, duration]);

  return (
    <span ref={ref}>
      {count}{suffix}
    </span>
  );
}

const stats = [
  { target: 500, suffix: '+', label: 'Proiecte realizate' },
  { target: 100, suffix: '%', label: 'Materiale premium' },
  { target: 98, suffix: '%', label: 'Clienți mulțumiți' },
  { target: 15, suffix: '+', label: 'Ani experiență' },
];

export default function AboutSection() {
  return (
    <section id="despre" data-testid="about-section" className="py-20 lg:py-32 bg-white dark:bg-[#242322]">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <AnimateOnView>
            <div className="relative">
              <div className="aspect-[4/5] rounded-lg overflow-hidden">
                <img
                  src={CRAFT_IMG}
                  alt="Meșteșug și atenție la detalii"
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#A67B5B] text-white p-6 rounded-lg hidden lg:block">
                <p className="font-['Outfit'] text-3xl font-semibold">15+</p>
                <p className="text-sm text-white/80">ani de experiență</p>
              </div>
            </div>
          </AnimateOnView>

          <div>
            <AnimateOnView delay={0.1}>
              <p className="text-sm uppercase tracking-[0.2em] font-semibold text-[#A67B5B] mb-4">
                Despre noi
              </p>
            </AnimateOnView>

            <AnimateOnView delay={0.2}>
              <h2 className="font-['Outfit'] text-3xl sm:text-4xl tracking-tight font-medium text-[#2C2A29] dark:text-[#F0EDE8] mb-6">
                Pasiune pentru lemn,
                <br />dedicare pentru tine
              </h2>
            </AnimateOnView>

            <AnimateOnView delay={0.3}>
              <p className="text-base leading-relaxed text-[#6B6561] dark:text-[#9B9590] mb-6">
                La <strong className="text-[#2C2A29] dark:text-[#F0EDE8]">Mobila ta Mob</strong>, credem că fiecare casă merită mobilier care reflectă personalitatea celor care o locuiesc. Cu peste 15 ani de experiență în domeniu, am transformat sute de spații în locuri de poveste — din atelierul nostru din <strong className="text-[#2C2A29] dark:text-[#F0EDE8]">Dumbrăveni, județul Suceava</strong>.
              </p>
            </AnimateOnView>

            <AnimateOnView delay={0.4}>
              <p className="text-base leading-relaxed text-[#6B6561] dark:text-[#9B9590] mb-8">
                Combinăm tehnicile tradiționale de tâmplărie cu designul modern, folosind doar materiale premium. Fiecare proiect începe cu o discuție deschisă, în care înțelegem nevoile tale, și se încheie cu un produs care depășește așteptările.
              </p>
            </AnimateOnView>

            <AnimateOnView delay={0.5}>
              <div className="grid grid-cols-2 gap-6">
                {stats.map((stat) => (
                  <div key={stat.label} className="border-l-2 border-[#A67B5B] pl-4">
                    <p className="font-['Outfit'] text-2xl font-semibold text-[#2C2A29] dark:text-[#F0EDE8]">
                      <AnimatedCounter target={String(stat.target)} suffix={stat.suffix} />
                    </p>
                    <p className="text-sm text-[#6B6561] dark:text-[#9B9590]">{stat.label}</p>
                  </div>
                ))}
              </div>
            </AnimateOnView>
          </div>
        </div>
      </div>
    </section>
  );
}
